import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import EllipsisHover from '@home/components/EllipsisHover';
import { getNewFeatureDisplayName, getNewFeatureDisplayDescription } from '../../../../utils';
import { DBSystemNameI18nKeyMap } from '@home/_config/config';
import { formatDateStrPlus } from '@intelligence/v3/utils/date';

import type { INewFeatureItem } from '@home/types/urlCollection';

import './index.scss';

import ICON_HOT from '@home/assets/svg/urlCollection/hot.svg';

interface INewFeatureListItemProps extends IBaseProps {
  item: INewFeatureItem;
  onClick?: (item: INewFeatureItem) => void;
}

const NewFeatureListItem: React.FC<INewFeatureListItemProps> = (props) => {
  const { style, className, item, onClick } = props;
  const { locale, formatMessage } = useIntl();
  const title = getNewFeatureDisplayName(locale, item);
  const subtitle = getNewFeatureDisplayDescription(locale, item);
  const itemSystem = item?.system || '';
  const itemOnlineTime = item?.online_time || '';
  const onlineTimeText = formatDateStrPlus(itemOnlineTime) || '';
  const systemTextId = itemSystem ? DBSystemNameI18nKeyMap[itemSystem] || '' : '';
  const backgroundStyles = item?.display_image ? { backgroundImage: `url('${item.display_image}')` } : {};
  const systemName = itemSystem
    ? formatMessage({
      id: systemTextId || 'dbh_databrain',
      defaultMessage: 'DataBrain',
    })
    : '';
  const isClickable = typeof onClick === 'function';
  const showSystemName = !!systemName;
  const showOnlineTime = !!onlineTimeText;

  const handleClick = (): void => {
    onClick?.(item);
  };

  return (
    <div
      style={style}
      className={classnames('dbh-new-feature-list-item', className, {
        'dbh-new-feature-list-item--clickable': isClickable,
      })}
      onClick={handleClick}
    >
      <div style={{ ...backgroundStyles }} className="dbh-new-feature-list-item__cover" />
      <div className="dbh-new-feature-list-item__content">
        <div className="dbh-new-feature-list-item__title-row">
          {!!item?.is_hot && <img src={ICON_HOT} alt="hot" className="dbh-new-feature-list-item__hot-icon" />}
          <div className="dbh-new-feature-list-item__title-main">
            <EllipsisHover className="dbh-new-feature-list-item__title" placement="top" text={title || '-'} />
            {isClickable && <SvgIcon className="dbh-new-feature-list-item__title-arrow" type="arrow-right" />}
          </div>
        </div>
        <EllipsisHover className="dbh-new-feature-list-item__subtitle" placement="top" text={subtitle || '-'} />
        {(showSystemName || showOnlineTime) && (
          <div className="dbh-new-feature-list-item__meta">
            {showSystemName && <span className="dbh-new-feature-list-item__meta-text">{systemName}</span>}
            {showSystemName && showOnlineTime && <span className="dbh-new-feature-list-item__meta-divider" />}
            {showOnlineTime && <span className="dbh-new-feature-list-item__meta-text">{onlineTimeText}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewFeatureListItem;
